import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

import Container from '../../../components/Container';
import AlertMsg from '../../../components/AlertMsg';

import { RestaurantSelector } from '../components';
import {
  CustomerList,
  CustomerGrowthChart,
  TopRestaurantsTable,
} from '../../Restaurant/customers/components';
import {
  useGetCustomers,
  useGetCustomerStats,
  useGetCustomerGrowth,
  useGetTopRestaurants,
} from '../../Restaurant/customers/services';
import { useExportCustomersExcel } from '../../Restaurant/customers/hooks/use-export-customers-excel';
import { useGetRestaurants } from '../services/restaurants-service';
import { TRestaurant } from '../schemas/restaurant-schema';

const GlobalCustomersPage = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0); // Default to Top Restaurants tab
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [msgAlert, setMsgAlert] = useState({ open: false, message: '', msgType: 'error' });

  // Restaurant selection
  const [selectedRestaurant, setSelectedRestaurant] = useState<TRestaurant | null>(null);
  const { data: restaurantsData, isLoading: restaurantsLoading, isError: restaurantsError } = useGetRestaurants();

  // Date range for growth chart (default: last 30 days)
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [fromDate, setFromDate] = useState(thirtyDaysAgo.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);

  // Top restaurants limit with debounced value
  const [topLimit, setTopLimit] = useState(10);
  const [debouncedTopLimit, setDebouncedTopLimit] = useState(10);

  // Debounce topLimit changes
  useEffect(() => {
    const timer = setTimeout(() => {
      // Validate: must be between 1 and 100
      const validLimit = Math.max(1, Math.min(100, topLimit));
      if (validLimit !== topLimit) {
        setTopLimit(validLimit);
      }
      setDebouncedTopLimit(validLimit);
    }, 500);

    return () => clearTimeout(timer);
  }, [topLimit]);

  // Fetch data (only if restaurant is selected)
  const restaurantId = selectedRestaurant?._id;
  const { data: customersData, isLoading: customersLoading, isError: customersError } = useGetCustomers(
    restaurantId ?? '',
    page,
    limit
  );
  const { data: stats, isError: statsError } = useGetCustomerStats(restaurantId);
  const { data: growthData, isLoading: growthLoading, isError: growthError } = useGetCustomerGrowth(
    fromDate,
    toDate,
    restaurantId
  );
  const { data: topRestaurants, isLoading: topRestaurantsLoading, isError: topRestaurantsError } =
    useGetTopRestaurants(debouncedTopLimit);

  const { exportToExcel, isPending: isExporting } = useExportCustomersExcel(setMsgAlert);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRestaurantChange = (restaurant: TRestaurant | null) => {
    setSelectedRestaurant(restaurant);
    setPage(1); // Reset to first page when changing restaurant
  };

  const handleTopLimitChange = useCallback((value: string) => {
    const numValue = Number(value);
    // Allow typing but enforce range
    if (numValue >= 0 && numValue <= 999) {
      setTopLimit(numValue);
    }
  }, []);

  const handleRestaurantClickFromTable = (restaurantId: string, restaurantName: string) => {
    // Find the full restaurant object from the list
    const restaurant = restaurantsData?.restaurants.find((r: TRestaurant) => r._id === restaurantId);

    if (restaurant) {
      setSelectedRestaurant(restaurant);
      setActiveTab(1); // Switch to Customer List tab (now at index 1)
      setPage(1); // Reset to first page
    } else {
      // If restaurant not found in list, create a minimal object
      setSelectedRestaurant({
        _id: restaurantId,
        title: restaurantName,
        name: restaurantName,
      } as TRestaurant);
      setActiveTab(1);
      setPage(1);
    }
  };

  const handleExportToExcel = () => {
    if (!selectedRestaurant?._id) {
      setMsgAlert({ open: true, message: 'No restaurant selected', msgType: 'warning' });
      return;
    }
    const restaurantName = selectedRestaurant?.title || selectedRestaurant?.name || 'Restaurant';
    exportToExcel(selectedRestaurant._id, restaurantName);
  };

  return (
    <Container page="customers" searchVal="" onSearch={() => null} searchLabel="">
      <Grid container item xs={12}>
        <Grid item xs={12} mb={2}>
          <Box sx={styles.header}>
            <Typography sx={styles.title}>Customers Management</Typography>
            <Typography sx={styles.subtitle}>
              View and manage customers across all restaurants
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sx={styles.mainView} px={3} pt={2}>
          {/* Tabs Navigation */}
          <Box sx={styles.tabsContainer}>
            <Tabs value={activeTab} onChange={handleTabChange} sx={styles.tabs}>
              <Tab label="Top Restaurants" sx={styles.tab} />
              <Tab label="Customer List" sx={styles.tab} />
              <Tab label="Analytics" sx={styles.tab} />
            </Tabs>
          </Box>

          {/* Error Alert for Restaurants Loading */}
          {restaurantsError && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              Failed to load restaurants. Please refresh the page to try again.
            </Alert>
          )}

          {/* Restaurant Selector - Only show for Customer List and Analytics tabs */}
          {(activeTab === 1 || activeTab === 2) && (
            <RestaurantSelector
              restaurants={restaurantsData?.restaurants || []}
              selectedRestaurant={selectedRestaurant}
              onRestaurantChange={handleRestaurantChange}
              isLoading={restaurantsLoading}
            />
          )}

          {/* Show empty state only for Customer List and Analytics tabs when no restaurant is selected */}
          {(activeTab === 1 || activeTab === 2) && !selectedRestaurant ? (
            <Box sx={styles.emptyState}>
              <Typography sx={styles.emptyStateText}>
                Please select a restaurant to view customer data
              </Typography>
            </Box>
          ) : (
            <>
              {/* Tab Content */}
              <Box sx={styles.tabContent}>
                {/* Tab 1: Top Restaurants */}
                {activeTab === 0 && (
                  <Box>
                    {/* Error Alert */}
                    {topRestaurantsError && (
                      <Alert severity="error" sx={{ marginBottom: 2 }}>
                        Failed to load top restaurants. Please try again.
                      </Alert>
                    )}

                    {/* Top Limit Selector */}
                    <Box sx={styles.topLimitContainer}>
                      <TextField
                        label="Show Top"
                        type="number"
                        value={topLimit}
                        onChange={(e) => handleTopLimitChange(e.target.value)}
                        inputProps={{ min: 1, max: 100 }}
                        sx={styles.topLimitInput}
                      />
                    </Box>

                    {/* Top Restaurants Table */}
                    <TopRestaurantsTable
                      topRestaurants={topRestaurants || []}
                      isLoading={topRestaurantsLoading}
                      onRestaurantClick={handleRestaurantClickFromTable}
                    />
                  </Box>
                )}

                {/* Tab 2: Customer List */}
                {activeTab === 1 && selectedRestaurant && (
                  <Box>
                    {/* Error Alert */}
                    {customersError && (
                      <Alert severity="error" sx={{ marginBottom: 2 }}>
                        Failed to load customers. Please try again.
                      </Alert>
                    )}

                    <CustomerList
                      customers={customersData?.rows || []}
                      totalCount={customersData?.count || 0}
                      page={page}
                      limit={limit}
                      onPageChange={handlePageChange}
                      isLoading={customersLoading}
                      onExport={handleExportToExcel}
                      isExporting={isExporting}
                    />
                  </Box>
                )}

                {/* Tab 3: Analytics (Combined Statistics & Growth) */}
                {activeTab === 2 && selectedRestaurant && (
                  <Box>
                    {/* Error Alerts */}
                    {(growthError || statsError) && (
                      <Alert severity="error" sx={{ marginBottom: 2 }}>
                        Failed to load analytics data. Please try again.
                      </Alert>
                    )}

                    {/* Date Range Picker */}
                    <Box sx={styles.dateRangeContainer}>
                      <TextField
                        label="From Date"
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={styles.dateInput}
                      />
                      <TextField
                        label="To Date"
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={styles.dateInput}
                      />
                    </Box>

                    {/* Growth Chart with Stats */}
                    <CustomerGrowthChart
                      growthData={growthData || []}
                      isLoading={growthLoading}
                      totalCustomers={stats?.totalCustomers}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Grid>
      </Grid>

      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
      />
    </Container>
  );
};

export default GlobalCustomersPage;

const styles = {
  header: {
    padding: 2,
  },
  title: {
    fontFamily: 'GilroyBold',
    fontSize: 28,
    color: '#1a1a1a',
    marginBottom: 0.5,
  },
  subtitle: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: '#666',
  },
  mainView: {
    backgroundColor: '#FFF',
    borderRadius: '8px',
    minHeight: 'calc(100vh - 200px)',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    backgroundColor: '#f5f5f5',
    borderRadius: '12px',
    minHeight: '400px',
  },
  emptyStateText: {
    fontFamily: 'GilroySemiBold',
    fontSize: 16,
    color: '#888',
  },
  tabsContainer: {
    borderBottom: '1px solid #eee',
    marginBottom: 3,
  },
  tabs: {
    '& .MuiTabs-indicator': {
      backgroundColor: '#000',
    },
  },
  tab: {
    fontFamily: 'GilroyBold',
    fontSize: 14,
    textTransform: 'capitalize' as const,
    color: '#666',
    '&.Mui-selected': {
      color: '#000',
    },
  },
  tabContent: {
    paddingTop: 2,
  },
  dateRangeContainer: {
    display: 'flex',
    gap: 2,
    marginBottom: 3,
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  dateInput: {
    minWidth: '200px',
  },
  topLimitContainer: {
    marginBottom: 3,
  },
  topLimitInput: {
    maxWidth: '150px',
  },
};
